import { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { cookieUtils } from '@/utils/cookieUtils';
import { logger } from '@/lib/logger';

export type ABVariant = 'A' | 'B';

// Memory cache for the current session to prevent recalculation on every render
let variantCache = new Map<string, { variant: ABVariant, timestamp: number }>();
let impressionTracker = new Set<string>();

/**
 * Clears the memory cache for A/B testing. Useful for tests or session resets.
 */
export function clearABTestCache() {
  variantCache.clear();
  impressionTracker.clear();
  if (typeof window !== 'undefined') {
    delete (window as any)._scooterp_anon_id;
  }
}

const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Gets or creates a persistent anonymous ID for the user.
 * Uses localStorage with a cookie fallback for privacy/strict modes.
 */
function getAnonymousId(): string {
  if (typeof window === 'undefined') return 'server';
  
  // 1. Try Memory
  const memId = (window as any)._scooterp_anon_id;
  if (memId) return memId;

  // 2. Try LocalStorage
  let id = null;
  try {
    id = localStorage.getItem('scooterp_anon_id');
  } catch (err) {
    logger.debug('LocalStorage not available for anon ID');
  }

  // 3. Try Cookies
  if (!id) {
    id = cookieUtils.get('scooterp_anon_id');
  }

  // 4. Create New
  if (!id) {
    id = crypto.randomUUID();
    try {
      localStorage.setItem('scooterp_anon_id', id);
    } catch (e) {}
    cookieUtils.set('scooterp_anon_id', id, 365); // 1 year
  }

  // Cache in memory for this window session
  (window as any)._scooterp_anon_id = id;
  return id;
}

/**
 * Hook to manage A/B testing variants persistently for a user and record real events.
 * Implements a cache and fallback mechanism to prevent recalculations and loops.
 */
export function useABTesting(experimentId: string, experimentDbId?: string): ABVariant {
  const location = useLocation();
  const anonId = useMemo(() => getAnonymousId(), []);
  
  const variant = useMemo(() => {
    if (typeof window === 'undefined') return 'A';
    
    const now = Date.now();

    // 0. Check Memory Cache
    if (variantCache.has(experimentId)) {
      const cached = variantCache.get(experimentId)!;
      if (now - cached.timestamp < CACHE_EXPIRATION_MS) {
        return cached.variant;
      }
    }

    // Manual overrides/finished tests
    const winners: Record<string, ABVariant> = {
      'old_button_test': 'B',
    };
    if (winners[experimentId]) return winners[experimentId];

    const storageKey = `scooterp_ab_${experimentId}`;
    let saved: string | null = null;
    let savedTimestamp: number = 0;
    
    // 1. Try LocalStorage
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.variant && parsed.timestamp) {
          saved = parsed.variant;
          savedTimestamp = parsed.timestamp;
        } else {
          // Legacy format fallback
          saved = raw;
          savedTimestamp = now;
        }
      }
    } catch (err) {}

    // 2. Try Cookies
    if (!saved) {
      saved = cookieUtils.get(storageKey);
      savedTimestamp = now; // Cookies don't store timestamp easily, assume fresh or fix later
    }

    // Check expiration
    if (saved && (saved === 'A' || saved === 'B')) {
      if (now - savedTimestamp < CACHE_EXPIRATION_MS) {
        variantCache.set(experimentId, { variant: saved as ABVariant, timestamp: savedTimestamp });
        return saved as ABVariant;
      }
    }
      
    // 3. Assign New Variant (Deterministic based on anonId)
    const newVariant: ABVariant = (anonId.charCodeAt(0) % 2 === 0) ? 'A' : 'B';
    const cacheEntry = { variant: newVariant, timestamp: now };
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(cacheEntry));
    } catch (e) {}
    cookieUtils.set(storageKey, newVariant, 1); // 1 day cookie
    
    variantCache.set(experimentId, cacheEntry);
    return newVariant;
  }, [experimentId, anonId]);

  useEffect(() => {
    // Only track impression once per experiment + path + variant per session
    const trackKey = `${experimentId}:${location.pathname}:${variant}`;
    if (impressionTracker.has(trackKey)) return;

    const recordImpression = async () => {
      impressionTracker.add(trackKey);
      
      logger.info(`[AB Testing] Recording impression for ${experimentId} variant ${variant} at ${location.pathname}`);

      // 1. Local/GA tracking
      trackEvent({
        action: 'ab_test_impression',
        category: 'cro',
        label: `${experimentId}_${variant}`,
        experiment_id: experimentId,
        variant: variant,
        page_path: location.pathname,
        is_first_in_session: impressionTracker.size === 1
      });

      // 2. Real Database tracking
      try {
        const urlParams = new URLSearchParams(window.location.search);
        await supabase.from('portal_analytics_events').insert({
          event_type: 'impression',
          page_path: location.pathname,
          variant_id: variant,
          experiment_id: experimentDbId,
          anonymous_id: anonId,
          source: urlParams.get('utm_source') || document.referrer || 'direct',
          campaign: urlParams.get('utm_campaign'),
          medium: urlParams.get('utm_medium'),
          device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: navigator.userAgent.split(' ')[0]
        });
      } catch (err) {
        logger.error('Error recording analytics impression:', err);
      }

      // Store in session for conversion attribution
      try {
        const activeExps = JSON.parse(sessionStorage.getItem('scooterp_active_experiments') || '{}');
        activeExps[experimentId] = { variant, dbId: experimentDbId };
        sessionStorage.setItem('scooterp_active_experiments', JSON.stringify(activeExps));
      } catch (e) {}
    };

    recordImpression();
  }, [variant, experimentId, experimentDbId, location.pathname, anonId]);

  return variant;
}

/**
 * Tracks a conversion event with all currently active A/B variants in the DB.
 */
export async function trackABConversion(action: string, label: string, metadata: any = {}) {
  const activeExps = JSON.parse(sessionStorage.getItem('scooterp_active_experiments') || '{}');
  const anonId = getAnonymousId();
  
  // Track in GA/Local
  trackEvent({
    action,
    category: 'conversion',
    label,
    experiments: activeExps,
    page_path: window.location.pathname
  });

  // Track in Supabase for each active experiment or as a general conversion
  try {
    const commonData = {
      event_type: action === 'signup' ? 'signup' : 'conversion',
      page_path: window.location.pathname,
      anonymous_id: anonId,
      metadata: { ...metadata, label },
      device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
    };

    const experiments = Object.values(activeExps);
    if (experiments.length > 0) {
      const inserts = experiments.map((exp: any) => ({
        ...commonData,
        variant_id: exp.variant,
        experiment_id: exp.dbId
      }));
      await supabase.from('portal_analytics_events').insert(inserts);
    } else {
      await supabase.from('portal_analytics_events').insert(commonData);
    }
  } catch (err) {
    console.error('Error recording conversion:', err);
  }
}
